import { defineStore } from "pinia";
import { ErrorMessageBag } from "@/stores/errors/ErrorMessageBag.ts";
import { Organisation } from "@/models/Organisation.model";
import { api } from "@/utils/Api.util";
import { PaginationState } from "@/models/PaginationState.model";
import { ApiErrorMessageBag } from "@/stores/errors/ApiErrorMessageBag.ts";
import { OrderParameter } from "@/models/OrderParameter.model";
import { useDefaultOrderStore } from "@/stores/DefaultOrder.store";
import { useGoalsStore } from "./Goals.store";
import { OrganisationFilterDef } from "./filters/OrganisationFilterDef";
import HubspotClient from "@/utils/HubspotClient.util.ts";
import { Curriculum } from "@/models/Curriculum.model";
import type { Goal } from "@/models/Goal.model";

interface OrganisationStoreState {
	errors: ErrorMessageBag | null,
	currentOrganisation: Organisation | null,
	organisations: Organisation[],
	currentPage: PaginationState | null,
	currentFilters: OrganisationFilterDef | null,
	order: OrderParameter
}

export const useOrganisationsStore = defineStore('organisations', {

	state: (): OrganisationStoreState => ({
		errors: null,
		currentOrganisation: null,
		organisations: [],
		currentPage: null,
		currentFilters: null,
		order: useDefaultOrderStore().organisationsOrder
	}),

	getters: {
		/**
		 * Scope a list of goals to the current organisation's curriculum.
		 *
		 * Lesson plans may carry goals from other curricula (external plans, or plans kept
		 * after a curriculum switch). Teachers must never see or evaluate those, and with no
		 * curriculum configured they must not see any goals at all — so this returns an
		 * empty list in that case.
		 */
		curriculumGoals(state): (goals: Goal[]) => Goal[] {
			const curriculum = state.currentOrganisation?.curriculum ?? null;
			return (goals: Goal[]) => Curriculum.filterGoals(goals, curriculum);
		},

		/**
		 * Whether the current user may act (evaluate, observe, …) in the given classroom.
		 *
		 * `users/me` only lists the classrooms the user may manage — every classroom for
		 * admins/owners, the taught ones for teachers — so the organisation's classroom
		 * list doubles as the access list.
		 */
		canManageClassroom(state): (classroomId: string) => boolean {
			const classrooms = state.currentOrganisation?.classrooms ?? [];
			return (classroomId: string) => classrooms.some(classroom => classroom.id === classroomId);
		},
	},

	actions: {

		new() {
			let newOrg = new Organisation();
			newOrg.locale = 'nl-BE';
			newOrg.subscription_plan = 'essential';
			newOrg.subscription_type = 'trial';
			newOrg.agenda_settings = {
				include_weekends: false,
				school_hours_start: undefined,
				school_hours_end: undefined
			}
			return newOrg;
		},

		async load(page: PaginationState | null = null, filter: OrganisationFilterDef | null = null) {

			const params: any = {
				mask: [
					'*',
					'owner.*',
					'agenda_settings.*',
				].join(','),
				page: page ? page.currentPage : null,
			};

			if (this.order) {
				params.order = this.order.getApiProperty();
			}

			if (filter) {
				Object.assign(params, filter.toApiParams());
			}

			this.organisations = [];
			const response = await api.get('organisations', {
				params: params
			});

			this.organisations = response.data.data.map(Organisation.mapFromServer);
			this.currentPage = PaginationState.mapFromServer(response.data.meta);
			this.currentFilters = filter;

		},

		/**
		 * Load all 'group' (umbrella) organisations, for the school-group selector.
		 */
		async loadGroupOrganisations(): Promise<Organisation[]> {
			const response = await api.get('organisations', {
				params: {
					mask: 'id,name',
					type: 'group',
					recordsPerPage: 500,
				}
			});
			return response.data.data.map(Organisation.mapFromServer);
		},

		changeOrder(order: OrderParameter) {
			this.order = order;
			this.load(this.currentPage, this.currentFilters);

			useDefaultOrderStore().setDefaultOrganisationsOrder(order);
		},

		async nextPage() {
			if (!this.currentPage) {
				return;
			}
			return await this.load(this.currentPage.getNext());
		},

		async previousPage() {
			if (!this.currentPage) {
				return;
			}
			return await this.load(this.currentPage.getPrevious());
		},

		async loadCurrentOrganisation() {

			const response = await api.get('organisation', {
				params: {
					mask: '*,features.*'
				}
			});

			this.setCurrentOrganisation(Organisation.mapFromServer(response.data.data));

		},

		setCurrentOrganisation(organisation: Organisation) {
			this.currentOrganisation = organisation;

			const goalsStore = useGoalsStore();
			if (organisation.curriculum) {
				goalsStore.setCurriculum(organisation.curriculum);
			}
		},

		/**
		 * Always returns the current organisation. Throws an error if no current organisation is set.
		 * @returns 
		 */
		getCurrentOrganisation(): Organisation {
			if (!this.currentOrganisation) {
				throw new Error('No current organisation set');
			}
			return this.currentOrganisation;
		},

		clearErrors() {
			this.errors = null;
		},

		switchToOrganisation(organisation: Organisation) {
			if (!organisation.id) {
				throw new Error('Organisation has no id');
			}

			localStorage.setItem('organisation_id', organisation.id);
			window.location.href = '/';
		},

		async save(organisation: Organisation) {
			this.clearErrors();
			try {
				const response = await api.put(
					'organisations/' + organisation.id,
					organisation.getServerData()
				);
				this.updateStoreItem(organisation);
			} catch (e) {
				this.errors = ApiErrorMessageBag.fromAxiosException(e);
				throw this.errors;
			}
		},

		async create(organisation: Organisation, email: string, sendInvitation: boolean) {
			this.clearErrors();
			try {
				const response = await api.post(
					'organisations',
					{
						...organisation.getServerData(),
						email: email,
						send_invitation: sendInvitation
					},
					{
						params: {
							mask: [
								'*',
								'owner.*'
							].join(','),
						}
					}
				);
				organisation.setFromServerData(response.data.data);
				this.addStoreItem(organisation);
			} catch (e) {
				this.errors = ApiErrorMessageBag.fromAxiosException(e);
				throw this.errors;
			}
		},

		async delete(organisation: Organisation) {

			this.clearErrors();
			try {
				await api.delete(
					'organisations/' + organisation.id
				);

				const index = this.organisations.findIndex(v => v.id === organisation.id);
				if (index >= 0) {
					this.organisations.splice(index, 1);
				}
			} catch (e) {
				this.errors = ApiErrorMessageBag.fromAxiosException(e);
				throw this.errors;
			}

		},

		updateStoreItem(organisation: Organisation) {
			let storeIndex = this.organisations.findIndex(
				storeItem => storeItem.id == organisation.id
			);

			if (storeIndex > -1) {
				this.organisations[storeIndex] = organisation.clone();
			}
		},

		addStoreItem(organisation: Organisation) {
			this.organisations.push(organisation);
		},

		/**
		 * Look for the organisation in Hubspot and prefill data if found.
		 * @param organisation
		 * @return true|string|null True if data was filled, string with error message if not found or error, null if no hubspot_id is set or hubspot is not configured.
		 */
		async prefillHubspotData(organisation: Organisation) {

			if (!organisation.hubspot_id) {
				return null;
			}

			const client = new HubspotClient();

			try {
				const hubspotData = await client.getCompany(organisation.hubspot_id);

				const properties = hubspotData.properties;

				if (!organisation.name) {
					organisation.name = properties.name;
				}

				return true;

			} catch (e) {
				if (e.status === 404) {
					// Company not found
					return 'Company not found';
				} else if (e.status === 410) {
					// Hubspot is not configured for this environment
					return null;
				}

				return 'Server error';
			}

		}
	}

});
